import Vue from 'vue'
import VueRouter from 'vue-router'
import { isLoggedIn, getUserInfo, getUserMenuPaths, hasMenuPermission } from '@/utils/auth'
import { Message } from 'element-ui'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { title: '登录' }
  },
  {
    path: '/',
    component: () => import('@/views/Layout.vue'),
    redirect: '/workbench',
    meta: { requiresAuth: true },
    children: [
      { path: 'workbench', name: 'Workbench', component: () => import('@/views/Workbench.vue'), meta: { title: '我的工作台', workbench: true } },
      { path: 'after-sales', name: 'AfterSales', component: () => import('@/views/AfterSales.vue'), meta: { title: '售后中心', afterSales: true } },
      {
        path: 'system-settings',
        name: 'SystemSettings',
        component: () => import('@/views/SystemSettings.vue'),
        meta: { title: '系统设置', adminOnly: true }
      },
      {
        path: 'home',
        name: 'Home',
        component: () => import('@/views/Home.vue'),
        meta: { title: '首页', icon: 'home' }
      },
      {
        path: 'product',
        name: 'Product',
        component: () => import('@/views/Product.vue'),
        meta: { title: '商品中心', icon: 'goods' }
      },
      {
        path: 'category',
        name: 'CategoryManage',
        component: () => import('@/views/CategoryManage.vue'),
        meta: { title: '商品类别', icon: 'menu', roles: ['admin'] }
      },
      {
        path: 'category/spu',
        name: 'SpuCategory',
        component: () => import('@/views/CategoryManage.vue'),
        meta: { title: '款式分类', roles: ['admin'] }
      },
      {
        path: 'size-template',
        name: 'SizeTemplate',
        component: () => import('@/views/SizeTemplate.vue'),
        meta: { title: '尺码表管理', icon: 's-operation', roles: ['admin', 'purchaser'] }
      },
      {
        path: 'supplier',
        name: 'Supplier',
        component: () => import('@/views/Supplier.vue'),
        meta: { title: '供应商管理', icon: 's-custom', roles: ['admin'] }
      },
      {
        path: 'inbound',
        name: 'Inbound',
        component: () => import('@/views/Inbound.vue'),
        meta: { title: '入库管理', icon: 'download', roles: ['purchaser', 'admin'] }
      },
      {
        path: 'outbound',
        name: 'Outbound',
        component: () => import('@/views/Outbound.vue'),
        meta: { title: '出库管理', icon: 'upload', roles: ['warehouse_keeper', 'admin'] }
      },
      {
        path: 'return',
        name: 'Return',
        component: () => import('@/views/Return.vue'),
        meta: { title: '退货管理', icon: 'refresh' }
      },
      {
        path: 'order',
        name: 'Order',
        component: () => import('@/views/Order.vue'),
        meta: { title: '订单管理', icon: 'list', roles: ['admin'] }
      },
      {
        path: 'order/distribution',
        name: 'OrderDistribution',
        component: () => import('@/views/OrderDistribution.vue'),
        meta: { title: '订单分布', icon: 'pie-chart', roles: ['admin'] }
      },
      {
        path: 'inventory/purchase-order',
        name: 'PurchaseOrder',
        component: () => import('@/views/PurchaseOrder.vue'),
        meta: { title: '采购单', icon: 'shopping-cart-full', roles: ['purchaser', 'admin'] }
      },
      {
        path: 'inventory/create-purchase',
        name: 'CreatePurchase',
        component: () => import('@/views/CreatePurchase.vue'),
        meta: { title: '创建采购单', roles: ['admin', 'warehouse_keeper'] }
      },
      {
        path: 'inventory/list',
        name: 'InventoryList',
        component: () => import('@/views/InventoryList.vue'),
        meta: { title: '库存列表', icon: 'box' }
      },
      {
        path: 'log',
        name: 'Log',
        component: () => import('@/views/Log.vue'),
        meta: { title: '操作日志', icon: 'document' }
      },
      {
        path: 'user',
        name: 'User',
        component: () => import('@/views/User.vue'),
        meta: { title: '用户管理', icon: 'user', roles: ['admin'] }
      },
      {
        path: 'reason-manage',
        name: 'ReasonManage',
        component: () => import('@/views/ReasonManage.vue'),
        meta: { title: '原因管理', icon: 'edit', roles: ['admin'] }
      },
      {
        path: 'menu-manage',
        name: 'MenuManage',
        component: () => import('@/views/MenuManage.vue'),
        meta: { title: '菜单管理', icon: 'menu', roles: ['admin'] }
      }
    ]
  }
]

const router = new VueRouter({
  mode: 'hash',
  routes
})

// 路由守卫
router.beforeEach((to, from, next) => {
  // 设置页面标题
  document.title = to.meta.title ? `${to.meta.title} - ERP管理系统` : 'ERP管理系统'
  
  // 检查是否需要登录
  // 子路由的 meta 不会自动继承父路由，必须检查完整 matched 链。
  const requiresAuth = to.matched.some(record => record.meta && record.meta.requiresAuth)
  if (requiresAuth) {
    if (!isLoggedIn()) {
      next('/login')
      return
    }
    
    const userInfo = getUserInfo()
    if (!userInfo) {
      next('/login')
      return
    }
    
    // 管理员拥有所有权限
    if (userInfo.role === 'admin') {
      next()
      return
    }
    if (to.matched.some(record => record.meta && record.meta.adminOnly)) {
      Message.error('仅管理员可以访问系统设置')
      next(false)
      return
    }
    
    if (to.meta.workbench) { next(); return }
    if (to.meta.afterSales) {
      if (['after_sales', 'warehouse_keeper'].includes(userInfo.role)) next()
      else { Message.error('无售后访问权限'); next(false) }
      return
    }
    // 排除登录页和首页（这些页面通常不需要菜单权限）
    if (to.path === '/login' || to.path === '/home' || to.path === '/') {
      next()
      return
    }

    // 首次进入时菜单尚未加载，先进入首页，由 Layout 拉取权限后再导航。
    if (getUserMenuPaths() === null) {
      next('/home')
      return
    }
    
    // 优先检查数据库菜单权限（如果用户有菜单权限，则允许访问）
    if (hasMenuPermission(to.path)) {
      next()
      return
    }
    
    // 数据库菜单权限是普通用户的唯一页面授权来源。
    Message.error('权限不足，无法访问此页面')
    next(false)
    return
  }
  
  // 如果已登录，不允许访问登录页
  if (to.path === '/login' && isLoggedIn()) {
    next('/')
    return
  }
  
  next()
})

export default router
